import {
  all,
  put,
  call,
  cancelled,
  takeEvery,
  select,
  take,
  fork,
  race,
} from 'redux-saga/effects';
import * as loadImage from 'blueimp-load-image';

import {
  attachActions,
  attachLinksSelector,
  attachPhotosSelector,
  uploadingVideoSelector,
} from './index';
import {
  LoadLinkAction,
  SaveUploadedVideoAction,
  UploadPhotosAction,
  UploadVideoAction,
} from './interface';
import { toastActions } from '../toast';
import { RootState } from '../rootReducer';
import { Api } from '$api';
import {
  ATTACHMENTS_COUNT_LIMIT,
  canvasToBlob,
  formatFileSize,
  readFile,
  statReachGoal,
} from '$utils';
import {
  shouldUseMultipart,
  MAX_FILE_SIZE_UNVERIFIED,
  MAX_FILE_SIZE_VERIFIED,
  isVideoFile,
  detectVideoContentType,
  sniffFile,
} from '$utils/multipartUpload';
import {
  abortSession,
  apiWithRetry,
  createPutTask,
  getUploadSessionById,
  multipartUploadSaga,
  runWithRetry,
  UploadReporter,
  watchProgress,
} from './multipartUpload';
import { apiOptions, classifyUploadError, isRetryableKind } from '$utils/uploadRetry';
import { setUploadLock } from '$utils/uploadLock';
import { uploadFingerprint } from '$utils/uploadSessions';
import { listenUploadEventsFlush, trackUploadEvent } from '$utils/uploadEvents';
import { batchActions } from '$store';
import { t } from '$translation/helper';
import { currentUserSelector, mainSelector } from '$store/main';
import { buffers, channel } from 'redux-saga';
import { reelsActions } from '$store/reels';
import { AttachUploadingVideo, UserModel } from '$store/models';
import { profileActions } from '$store/profile';
import { goBack } from '$navigation/helper';

function loadImageByUrl(url) {
  return new Promise((resolve, reject) => {
    let img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };

    img.onerror = () => {
      reject();
    };

    img.src = url;
  });
}

export function dataURLtoBlob(dataurl) {
  var arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

function* uploadPhotosWorker(action: UploadPhotosAction) {
  try {
    const { pickerId, files, asPeerId = 0 } = action.payload;
    const limit = action.payload.limit || ATTACHMENTS_COUNT_LIMIT;
    const photos = yield select((state: RootState) =>
      attachPhotosSelector(state, pickerId),
    );
    const user = yield select(currentUserSelector);
    const { isWatermarkDisabled } = yield select(mainSelector);
    const isGroup = pickerId.includes('-');

    let preparedPhotos: {
      photoId: number;
      base64: string;
      blob: Blob;
      width: number;
      height: number;
    }[] = [];

    if (photos.length + files.length > limit) {
      return yield put(
        toastActions.setToastFail(t('photos_limit_error_custom', { limit })),
      );
    }

    let startId = Date.now();
    for (let file of files) {
      if (file.type.substr(0, 6) !== 'image/') {
        return yield put(toastActions.setToastFail(t('photos_bad_error')));
      }

      let width, height, blob, base64;
      if (file.type === 'image/gif') {
        const dataUrl = yield call(readFile, file);
        const imgData = yield call(loadImageByUrl, dataUrl);

        base64 = dataUrl;
        width = imgData.width;
        height = imgData.height;
        blob = yield call(dataURLtoBlob, dataUrl);
      } else {
        const data = yield call(loadImage, file, {
          maxWidth: 1920,
          maxHeight: 1920,
          meta: true,
          canvas: true,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
          orientation: true,
        });

        if (!isWatermarkDisabled && !isGroup && action.payload.watermark !== false) {
          const ctx = data.image.getContext('2d');

          const scale = (data.image.height + data.image.width) / 1920;

          ctx.font = `bold ${50 * scale}px 'Nunito Sans'`;
          ctx.textBaseline = 'bottom';
          ctx.textAlign = 'right';
          ctx.fillStyle = 'rgba(179, 179, 179, 0.5)';
          const text = `${location.host}/${user.domain ? user.domain : `id${user.id}`}`;
          const textData = ctx.measureText(text);

          ctx.fillText(
            text,
            data.image.width - 20 * scale,
            data.image.height - 16 * scale,
            textData.width,
          );
          ctx.strokeStyle = 'rgba(0,0,0,0.3)';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowBlur = 1;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          ctx.strokeWidth = 1;
          ctx.strokeText(
            text,
            data.image.width - 20 * scale,
            data.image.height - 16 * scale,
            textData.width,
          );
        }

        const quality = 0.92;
        base64 = data.image.toDataURL('image/jpeg', quality);
        blob = yield call(canvasToBlob, data.image, quality);
        width = data.image.width;
        height = data.image.height;
      }

      preparedPhotos.push({
        photoId: -++startId,
        base64,
        blob,
        width,
        height,
      });
    }

    yield put(
      attachActions.pushPhotos({
        pickerId,
        photos: preparedPhotos.map((photo) => ({
          photoMedium: photo.base64,
          photoLarge: photo.base64,
          width: photo.width,
          height: photo.height,
          photoId: photo.photoId,
        })),
      }),
    );

    for (let photo of preparedPhotos) {
      const form = new FormData();
      form.append('file', photo.blob);
      form.append('album_id', `-3`); // -3 is attachments album_id
      if (asPeerId) form.append('asPeerId', String(asPeerId));
      try {
        const data = yield call(Api.post, '/photos/upload', form);
        yield put(
          attachActions.updatePhoto({
            pickerId,
            photoId: photo.photoId,
            updated: {
              photoId: +data.photo_id,
              thumbnail: data.thumbnail,
              path: data.path,
            },
          }),
        );
      } catch (e) {
        yield put(toastActions.setToastFail(e.message));
        yield put(
          attachActions.removePhoto({
            pickerId,
            photoId: photo.photoId,
          }),
        );
      }
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadLinkWorker(action: LoadLinkAction) {
  const { pickerId, link } = action.payload;
  try {
    const attachedLink = yield select((state: RootState) =>
      attachLinksSelector(state, pickerId),
    );
    if (attachedLink) {
      return;
    }

    const data = yield call(Api.post, '/attach/link', {
      link,
    });

    yield put(
      batchActions(
        attachActions.setLink({
          pickerId,
          link: data,
        }),
        attachActions.setLoadedLink(pickerId),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(
        attachActions.setLoadedLink(pickerId),
        attachActions.setLinkFailed(link),
      ),
    );
  }
}

const uploadReporter: UploadReporter = {
  progress: attachActions.setUploadingVideoProgress,
  status: attachActions.setUploadingVideoStatus,
  uploadId: attachActions.setUploadingVideoId,
};
const saveReporter: UploadReporter = { ...uploadReporter, idleStatus: 'saving' };

// Files below the multipart threshold go up in one PUT; on failure the whole
// file is simply sent again (it is small), with the same wait-for-network policy.
function* singlePutUploadSaga(file: File, asPeerId: number) {
  const contentType = yield call(detectVideoContentType, file);
  const requestUrl = () =>
    call(apiWithRetry, '/video/upload_url', { contentType, asPeerId }, uploadReporter);
  let uploadData = yield requestUrl();
  const progressChan = channel(buffers.sliding(1));
  yield fork(watchProgress, progressChan, attachActions.setUploadingVideoProgress);
  const put = createPutTask((event) =>
    progressChan.put({ loaded: event.loaded, total: event.total }),
  );
  let attempts = 0;

  try {
    yield call(
      runWithRetry,
      () => put.run(file, uploadData.url, file, uploadData.headers),
      {
        reporter: uploadReporter,
        refresh: function* () {
          uploadData = yield requestUrl();
        },
        onAttempt: () => {
          attempts++;
        },
      },
    );
    return { uploadData, stats: { attempts } };
  } finally {
    progressChan.close();
    if (yield cancelled()) {
      put.cancel();
    }
  }
}

function uploadEventBase(file: File, mode: string, startedAt: number) {
  return {
    mode,
    file_size: file.size,
    duration_ms: Date.now() - startedAt,
  };
}

function* runVideoUpload(
  file: File,
  asPeerId: number,
  fingerprint: string | undefined,
  resumeUploadId: string | undefined,
) {
  const mode = shouldUseMultipart(file.size) ? 'multipart' : 'single';
  const startedAt = Date.now();
  yield put(attachActions.setUploadingVideoStatus({ status: 'uploading' }));

  try {
    const { cancelledByUser } = yield race({
      completed: call(function* () {
        const { uploadData, stats } =
          mode === 'multipart'
            ? yield call(
                multipartUploadSaga,
                file,
                uploadReporter,
                asPeerId,
                fingerprint,
                resumeUploadId,
              )
            : yield call(singlePutUploadSaga, file, asPeerId);
        yield put(attachActions.setUploadingVideoUploadData(uploadData));
        yield put(attachActions.setUploadingVideoProgress({ isLoaded: true }));
        trackUploadEvent('video_upload_complete_client', {
          ...uploadEventBase(file, mode, startedAt),
          upload_id: stats.uploadId,
          attempts: stats.attempts,
          resumed_parts: stats.resumedParts,
          parts: stats.partsCount,
        });
      }),
      cancelledByUser: take(attachActions.cancelUploadingVideo),
    });
    if (cancelledByUser) {
      trackUploadEvent('video_upload_cancel', uploadEventBase(file, mode, startedAt));
    }
  } catch (e) {
    const kind = e.kind || classifyUploadError(e);
    if (kind === 'cancel') {
      return;
    }
    const { uploadId, loaded, total } = yield select(uploadingVideoSelector);
    yield put(
      attachActions.setUploadingVideoStatus({
        status: 'failed',
        error: { kind, message: e.message },
      }),
    );
    trackUploadEvent('video_upload_error', {
      ...uploadEventBase(file, mode, startedAt),
      upload_id: uploadId,
      error_kind: kind,
      http_status: e.response?.status,
      message: String(e.message || '').slice(0, 200),
      loaded,
      total,
    });
  }
}

function* uploadVideoWorker(action: UploadVideoAction) {
  try {
    const {
      pickerId,
      file,
      limit = ATTACHMENTS_COUNT_LIMIT,
      asPeerId = 0,
    } = action.payload;

    statReachGoal('video_upload_start', {
      fileSize: file.size,
    });
    listenUploadEventsFlush();
    setUploadLock(true);

    const user = (yield select(currentUserSelector)) as UserModel;

    yield put(
      attachActions.setUploadingVideo({
        video: file,
        loaded: 0,
        total: 0,
        isCancelled: false,
        isLoaded: false,
        pickerId,
        asPeerId,
      }),
    );

    const sniffed = yield call(sniffFile, file);
    if (sniffed.kind === 'image') {
      yield put(attachActions.cancelUploadingVideo());
      return yield put(
        toastActions.setToastFail(
          t('video_upload_image_error', {
            defaultValue:
              'This file is an image, not a video. Please select a video file',
          }),
        ),
      );
    }
    if (sniffed.kind === 'other') {
      yield put(attachActions.cancelUploadingVideo());
      return yield put(
        toastActions.setToastFail(
          t('video_upload_not_video_error', {
            defaultValue: 'The selected file is not a video',
          }),
        ),
      );
    }
    if (
      sniffed.kind === 'video_unsupported' ||
      (sniffed.kind === 'unknown' && !isVideoFile(file))
    ) {
      yield put(attachActions.cancelUploadingVideo());
      return yield put(toastActions.setToastFail(t('video_format_error')));
    }

    const maxSize =
      user.verified !== 0 ? MAX_FILE_SIZE_VERIFIED : MAX_FILE_SIZE_UNVERIFIED;

    if (file.size > maxSize) {
      yield put(attachActions.cancelUploadingVideo());
      return yield put(
        toastActions.setToastFail(
          t('video_size_error', { size: formatFileSize(maxSize) }),
        ),
      );
    }

    const photos = yield select((state: RootState) =>
      attachPhotosSelector(state, pickerId),
    );
    if (photos.length >= limit) {
      yield put(attachActions.cancelUploadingVideo());
      return yield put(
        toastActions.setToastFail(t('photos_limit_error_custom', { limit })),
      );
    }

    const fingerprint = user?.id ? uploadFingerprint(user.id, file, asPeerId) : undefined;
    yield call(runVideoUpload, file, asPeerId, fingerprint, undefined);
  } catch (e) {
    yield put(attachActions.clearUploadingVideoData());
    yield put(toastActions.setToastFail(e.message));
  }
}

function* retryUploadingVideoWorker() {
  const {
    video,
    asPeerId = 0,
    uploadId,
    status,
    error,
    loaded,
    total,
  } = (yield select(uploadingVideoSelector)) as AttachUploadingVideo;
  if (!video || status !== 'failed') {
    return;
  }
  const user = (yield select(currentUserSelector)) as UserModel;
  trackUploadEvent('video_upload_retry', {
    upload_id: uploadId,
    error_kind: error?.kind,
    file_size: video.size,
    loaded,
    total,
  });
  const fingerprint = user?.id ? uploadFingerprint(user.id, video, asPeerId) : undefined;
  const resumeUploadId =
    uploadId && getUploadSessionById(uploadId) ? uploadId : undefined;
  yield call(runVideoUpload, video, asPeerId, fingerprint, resumeUploadId);
}

function* cancelUploadingVideoWorker() {
  const {
    uploadId,
    isLoaded,
    video,
    error,
    asPeerId = 0,
  } = (yield select(uploadingVideoSelector)) as AttachUploadingVideo;
  // An unreadable file is re-selected by the user; keep the session so it resumes.
  if (uploadId && video && !isLoaded && error?.kind !== 'file_unreadable') {
    yield call(abortSession, uploadId, asPeerId);
  }
}

let lastSavePayload: SaveUploadedVideoAction['payload'] | null = null;
let lastCreateData: { key: string; data: any } | null = null;

function* retrySavingVideoWorker() {
  if (lastSavePayload) {
    trackUploadEvent('video_upload_save_retry', { key: lastSavePayload.uploadData.key });
    yield call(saveUploadedVideoWorker, attachActions.saveUploadedVideo(lastSavePayload));
  }
}

// Everything after the file is in S3: create the video record, upload the
// cover, attach it. Same wait-for-network policy; the video id is remembered
// so a retry never creates a second record.
function* saveUploadedVideoWorker(action: SaveUploadedVideoAction) {
  const { profileId, pickerId, uploadData, photo, asPeerId = 0 } = action.payload;
  if (!uploadData?.key || !uploadData?.bucket) {
    return;
  }
  lastSavePayload = action.payload;

  const form = new FormData();
  form.append('file', dataURLtoBlob(photo.photoMedium));
  form.append('album_id', `-3`);
  if (asPeerId) form.append('asPeerId', String(asPeerId));

  try {
    yield put(attachActions.setUploadingVideoStatus({ status: 'saving' }));
    yield put(
      attachActions.pushPhotos({
        pickerId,
        photos: [photo],
      }),
    );
    // The full create response (incl. reel) is kept for a retry, not just the id.
    let videoCreateData: any =
      lastCreateData?.key === uploadData.key ? lastCreateData.data : null;
    if (!videoCreateData) {
      videoCreateData = yield call(
        runWithRetry,
        () =>
          Api.post('/video/create', { ...uploadData, asPeerId }, apiOptions).then(
            (res: any) => {
              if (!res?.videoId) throw new Error('videoId not returned');
              return res;
            },
          ),
        { reporter: saveReporter },
      );
      lastCreateData = { key: uploadData.key, data: videoCreateData };
    }
    const uploadPhotoData = yield call(
      runWithRetry,
      () => Api.post('/photos/upload', form, apiOptions),
      { reporter: saveReporter },
    );
    yield call(
      runWithRetry,
      () =>
        Api.post(
          '/video/cover/change',
          {
            videoId: videoCreateData.videoId,
            photoId: uploadPhotoData.photo_id,
            asPeerId,
          },
          apiOptions,
        ),
      { reporter: saveReporter },
    );

    lastSavePayload = null;
    lastCreateData = null;
    if (yield call(isSaveModalOpen, uploadData.key)) {
      yield put(attachActions.setUploadingVideoStatus({ status: 'uploading' }));
      goBack();
    }

    yield put(
      attachActions.updatePhoto({
        pickerId,
        photoId: photo.photoId,
        updated: {
          photoId: +videoCreateData.videoId,
          photoMedium: photo.photoMedium,
          photoLarge: photo.photoLarge,
        },
      }),
    );

    if (videoCreateData.reel && profileId !== undefined) {
      yield put(reelsActions.addReel(videoCreateData.reel));
      yield call(statReachGoal, 'reel_created');
      yield put(profileActions.incrementReelsCount(profileId));
      yield put(reelsActions.loadList({ list: `profile_${profileId}` }));
      goBack();
    }
  } catch (e) {
    yield put(attachActions.removePhoto({ pickerId, photoId: photo.photoId }));
    const message =
      e.code === 'limit' && typeof e.params?.limit === 'number'
        ? t('reels_daily_limit_error', { limit: e.params.limit })
        : e.message;
    const kind = e.kind || classifyUploadError(e);
    trackUploadEvent('video_upload_save_error', {
      key: uploadData.key,
      error_kind: kind,
      message: String(message || '').slice(0, 200),
    });
    const modalOpen = yield call(isSaveModalOpen, uploadData.key);
    if (isRetryableKind(kind) && modalOpen) {
      yield put(
        attachActions.setUploadingVideoStatus({
          status: 'save_failed',
          error: { kind, message },
        }),
      );
      return;
    }
    lastSavePayload = null;
    if (modalOpen) {
      yield put(attachActions.cancelUploadingVideo());
    }
    yield put(toastActions.setToastFail(message));
  }
}

// The modal may have been closed, or reopened for another file, while a save ran.
function* isSaveModalOpen(key: string) {
  const { video, uploadData } = (yield select(
    uploadingVideoSelector,
  )) as AttachUploadingVideo;
  return Boolean(video) && uploadData?.key === key;
}

export function* attachSaga() {
  yield all([
    takeEvery(attachActions.uploadPhotos, uploadPhotosWorker),
    takeEvery(attachActions.loadLink, loadLinkWorker),
    takeEvery(attachActions.uploadVideo, uploadVideoWorker),
    takeEvery(attachActions.retryUploadingVideo, retryUploadingVideoWorker),
    takeEvery(attachActions.cancelUploadingVideo, cancelUploadingVideoWorker),
    takeEvery(attachActions.saveUploadedVideo, saveUploadedVideoWorker),
    takeEvery(attachActions.retrySavingVideo, retrySavingVideoWorker),
    takeEvery(attachActions.clearUploadingVideoData, () => {
      setUploadLock(false);
      lastSavePayload = null;
      lastCreateData = null;
    }),
  ]);
}
